import { _ } from "../../../core/polyglot/polyglot.js";
import { guid } from "../../../core/helpers/string-util.js";
import Model from "../../Model.js";
import reportColumnTemplateFactory from "./columnTemplates/reportColumnTemplateFactory.js";
import ReportDisplaySettings from "../reports/ReportDisplaySettings.js";
import reportRowTemplateFactory from "./rowTemplates/reportRowTemplateFactory.js";
import TranslatedString from "../../TranslatedString.js";

/**
 * Report template model.
 *
 * A report template holds the static data structure used to instantiate reports.
 * - row templates
 * - columns templates
 * - display settings used to render reports
 *
 * Reports are instantiated by interpreting report templates (see
 * `reportTemplate.interpret`).
 *
 * @param {TranslatedString} spec.title - Title of the report
 * @param {string} [spec.id=guid()] - Unique identifier
 * @param {string} [spec.description=''] - Description of the report
 * @param {Array} [spec.columns=[]] - List of template columns to be displayed
 * @param {Array} [spec.rows=[]] - List of template rows to be displayed (not
 * including accounts)
 * @param {reportDisplaySettings} [spec.displaySettings] - Default display
 * settings for instances
 * @param {boolean} [spec.isVisibleToAllUsers=true] - Indicates whether this
 * template(read report) is visible to all users of company. NB! This is
 * only a visibility(not alterability) setting.
 * @param {boolean} [spec.newTemplate=true] - Whether the template is a new
 * object (not in the database).
 * @param {boolean} [spec.showPeriodComparisonPicker] - Whether a comparing
 * period picker should be displayed in the report viewer.
 * @param {boolean} [spec.isKnown] - Whether the report is a
 * well-known one (e.g., "income statement")
 * @param {boolean} [spec.isBalanceSheet] - Whether the report
 * template represents a balance sheet report. When true,
 * preliminary result rows will be injected at interpretation
 * time.
 * @param {string} [ownerId] - Identifier for the entity (company,
 * company type or country) owning the receiver.
 * @param {string} [ownerType] - A value of ReportTemplateOwnerType.cs.
 * @param {string} [accountGroupIdForPreliminaryResult] - If the template is for
 * a balance sheet, specify where in the account groups displayed in the report
 * the preliminary result KPI rows should be injected. If not set, defaults to
 * the standard row injection  as defined in `TemplateInterpreter.js`.
 */
export default class ReportTemplate extends Model {
	constructor({
		title = "",
		description = "",
		columns = [],
		rows = [],
		id = guid(),
		displaySettings = new ReportDisplaySettings(),
		isVisibleToAllUsers = false,
		newTemplate = true,
		showPeriodComparisonPicker = false,
		isKnown = false,
		isBalanceSheet = false,
		accountGroupIdForPreliminaryResult = "",
		ownerId,
		ownerType,
	} = {}) {
		super(...arguments);
		this._id = id;
		this._title = title;
		this._description = description;
		this._displaySettings = displaySettings;
		this._isVisibleToAllUsers = isVisibleToAllUsers;
		this._newTemplate = newTemplate;
		this._showPeriodComparisonPicker = showPeriodComparisonPicker;
		this._isKnown = isKnown;
		this._isBalanceSheet = isBalanceSheet;

		this._accountGroupIdForPreliminaryResult =
			accountGroupIdForPreliminaryResult;

		this._ownerId = ownerId;
		this._ownerType = ownerType;

		this.setRows(rows);
		this.setColumns(columns);
	}

	getTitle() {
		return this._title;
	}

	getTitleString() {
		return this.getTitle().toString();
	}

	setTitle(title) {
		this._title = title;
	}

	getKnownName() {
		switch (this._id) {
			case "balance-sheet": {
				return _("Balance Sheet");
			}
			case "income-statement": {
				return _("Income Statement");
			}
			case "budget-report": {
				return _("Budget Report");
			}
			case "cash-flow-report": {
				return _("Cash Flow");
			}
			default: {
				return null;
			}
		}
	}

	getName() {
		return this.getTitle();
	}

	getDescription() {
		return this._description;
	}

	setDescription(description) {
		this._description = description;
	}

	getColumns() {
		return this._columns;
	}

	findColumnRecursively({ predicate }) {
		for (const column of this._columns) {
			if (predicate(column)) {
				return column;
			}

			const found = column.findColumnRecursively({
				predicate,
			});
			if (found) {
				return found;
			}
		}

		return undefined;
	}

	setColumns(columns) {
		this._columns = [];
		columns.forEach((column) => column.addTo(this));

		// Reference resolution is done in a second step. The reason is that
		// if a column is referenced by a previous column, we need to have the
		// data structure full built before we can start resolving.
		columns.forEach((column) => column.resolveReferences());
	}

	getRows() {
		return this._rows;
	}

	setRows(rows) {
		this._rows = [];
		rows.forEach((row) => row.addTo(this));

		// Reference resolution is done in a second step. The reason is that
		// if a row is referenced by a previous row, we need to have the
		// data structure full built before we can start resolving.
		rows.forEach((row) => row.resolveReferences());
	}

	getId() {
		return this._id;
	}

	getDisplaySettings() {
		return this._displaySettings;
	}

	isVisibleToAllUsers() {
		return this._isVisibleToAllUsers;
	}

	setVisibleToAllUsers(visibleToAllUsers) {
		this._isVisibleToAllUsers = visibleToAllUsers;
	}

	getAccountGroupIdForPreliminaryResult() {
		return this._accountGroupIdForPreliminaryResult;
	}

	isBalanceSheet() {
		return !!this._isBalanceSheet;
	}

	/**
	 * Return all rows from the template fetched recursively.
	 *
	 * @return {abstractRow[]} - Flatten array of rows.
	 */
	getAllRows() {
		const rows = this.getRows().slice();

		this._rows.forEach((row) => {
			// eslint-disable-next-line no-shadow -- bad practice, we should fix this
			row.getAllRows().forEach((row) => {
				rows.push(row);
			});
		});

		return rows;
	}

	getOptionalColumns() {
		return this.getColumns().filter((column) => column.isOptional());
	}

	getAllOptionalRows() {
		return this.getAllRows().filter((row) => row.isOptional());
	}

	getPeriodColumns() {
		return this.getColumns().filter((column) => column.isPeriodColumn());
	}

	showPeriodComparisonPicker() {
		return this._showPeriodComparisonPicker;
	}

	setShowPeriodComparisonPicker(boolean) {
		this._showPeriodComparisonPicker = boolean;
	}

	getDefaultExpansionLevel() {
		return this.getDisplaySettings().getDefaultExpansionLevel();
	}

	setDefaultExpansionLevel(newLevel) {
		this.getDisplaySettings().setDefaultExpansionLevel(newLevel);
	}

	isKnown() {
		return this._isKnown;
	}

	/**
	 * Return the depth of the template, which is always 0.
	 *
	 * First level row templates have a depth of one, their children a depth of 2,
	 * etc.
	 */
	getDepth() {
		return 0;
	}

	beSaved() {
		this._newTemplate = false;
	}

	/**
	 * Return all report template items that have a dependency to `item`.
	 *
	 * @return {abstractTemplateItem[]} - template items that reference `item`.
	 */
	getDependenciesTo(item) {
		return [
			...this.getAllRows().filter((row) => row.hasDependencyTo(item)),
			...this.getColumns().filter((column) =>
				column.hasDependencyTo(item),
			),
		];
	}

	/**
	 * Return true if the template has `item` either in its rows or columns.
	 */
	contains(item) {
		return (
			this.getAllRows().includes(item) || this.getColumns().includes(item)
		);
	}

	getItemById(id) {
		return this.getRowById(id) || this.getColumnById(id);
	}

	/**
	 * Find the row with the id `id`.
	 */
	getRowById(id) {
		return this.getAllRows().find((row) => row.getId() === id);
	}

	/**
	 * Find the column with the id `id`.
	 */
	getColumnById(id) {
		return this.getColumns().find((column) => column.getId() === id);
	}

	/**
	 * Collapse all account group and interval rows in the template.
	 */
	collapseDynamicGroupRows() {
		this.getAllRows()
			.filter(
				(row) =>
					row.isAccountGroupRow() || row.isAccountIntervalGroupRow(),
			)
			.forEach((row) => {
				row.setCollapsed(true);
			});
	}

	isDebitPositive() {
		return this._displaySettings.getDebitPositive();
	}

	isRepeated() {
		return false;
	}

	isRepeatingRowTemplate() {
		return false;
	}

	isVisible() {
		return true;
	}

	isNewTemplate() {
		return this._newTemplate;
	}

	hasAccountGroupRowTemplate() {
		return !!this.getAllRows().find((row) =>
			row.isAccountGroupRowTemplate(),
		);
	}

	validate({ collector, context }) {
		super.validate(...arguments);

		this.getAllRows().forEach((row) =>
			row.validate({ collector, context }),
		);

		this.getColumns().forEach((column) =>
			column.validate({ collector, context }),
		);
	}

	canAdd() {
		return true;
	}

	canHaveChildren() {
		return true;
	}

	canBeDeletedBy(owner) {
		return !this.isKnown() && this.belongsTo(owner);
	}

	canBeRestoredFor(owner) {
		return this.isKnown() && this.belongsTo(owner);
	}

	belongsTo(owner) {
		return this._ownerId === owner.getLegacyId();
	}

	hasOptionalRows() {
		return this.getAllOptionalRows().length > 0;
	}

	hasOptionalColumns() {
		return this.getOptionalColumns().length > 0;
	}

	/**
	 * Add `item` as a column or top-level row of the receiver.
	 *
	 * When `sibling` is provided, insert `item` right after `sibling` if
	 * `sibling` is a direct child of the receiver.
	 */
	add(item, sibling) {
		return item.addTo(this, sibling);
	}

	remove(item) {
		return item.removeFrom(this);
	}

	addColumn(column, sibling) {
		let position = this._columns.length;
		const siblingPosition = this._columnIndex(sibling);

		if (siblingPosition >= 0) {
			position = siblingPosition + 1;
		}

		this._columns.splice(position, 0, column);
		column.setReportTemplate(this);
	}

	removeColumn(column) {
		this._columns = this._columns.filter((col) => col !== column);
	}

	addRow(row, sibling) {
		let position = this._rows.length;
		const siblingPosition = this._rowIndex(sibling);

		if (siblingPosition >= 0) {
			position = siblingPosition + 1;
		}

		this._rows.splice(position, 0, row);
		row.setParent(this);
		row.setReportTemplate(this);
	}

	removeRow(row) {
		row.setParent(null);
		this._rows = this._rows.filter((r) => r !== row);
	}

	isRow() {
		return false;
	}

	/**
	 * Return a shallow copy of the report template.
	 */
	clone() {
		return new this.constructor({
			title: this._title,
			description: this._description,
			columns: this._columns.slice(),
			rows: this._rows.slice(),
			displaySettings: this._displaySettings,
			isBalanceSheet: this._isBalanceSheet,
			accountGroupIdForPreliminaryResult:
				this._accountGroupIdForPreliminaryResult,
		});
	}

	accept(visitor) {
		return visitor.visitReportTemplate(this);
	}

	_columnIndex(column) {
		return this._columns.indexOf(column);
	}

	_rowIndex(row) {
		return this._rows.indexOf(row);
	}

	_getMatchingFields() {
		return [this.getTitleString()];
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.id = this._id;
		properties.title = this._title;
		properties.description = this._description;
		properties.displaySettings = this._displaySettings;
		properties.isVisibleToAllUsers = this._isVisibleToAllUsers;
		properties.newTemplate = this._newTemplate;

		properties.showPeriodComparisonPicker =
			this._showPeriodComparisonPicker;

		properties.isKnown = this._isKnown;
		properties.isBalanceSheet = this._isBalanceSheet;

		properties.accountGroupIdForPreliminaryResult =
			this._accountGroupIdForPreliminaryResult;

		properties.ownerId = this._ownerId;
		properties.ownerType = this._ownerType;

		properties.columns = this._columns;
		properties.rows = this._rows;

		return properties;
	}

	static transformMaterializationData(data, context) {
		data.title = TranslatedString.materialize(data.title);

		data.rows = reportRowTemplateFactory.materializeAll(data.rows, context);

		data.columns = reportColumnTemplateFactory.materializeAll(
			data.columns,
			context,
		);

		data.displaySettings = ReportDisplaySettings.materialize(
			data.displaySettings,
		);

		data.newTemplate = false;
	}
}
